import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { DialogComponent } from '@vsolv/vectors-ui/dialog';
import { POLICY_DIMENSIONS_CONFIG } from '../../config/policy-dimensions.config';

interface Dimension {
  id?: string;
  title: string;
  description?: string;
  isScalar: boolean;
  isPricing?: boolean;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'ws-dimension-dialog',
  templateUrl: './dimension.dialog.html',
  standalone: false,
})
export class DimensionDialog {
  constructor(
    private formBuilder: FormBuilder,
    private toastSvc: ToastService,
    private http: HttpClient
  ) {}

  @ViewChild(DialogComponent) dialog!: DialogComponent;

  @Input() dimension?: Dimension | null = null;

  @Output() closed = new EventEmitter<boolean>();

  // Config
  readonly config = POLICY_DIMENSIONS_CONFIG.dimensionDialog;
  readonly commonConfig = POLICY_DIMENSIONS_CONFIG.common;

  saving = false;

  form = this.formBuilder.group({
    title: ['', [Validators.required, Validators.maxLength(255)]],
    description: ['', [Validators.maxLength(255)]],
    isScalar: [false, Validators.required],
    isPricing: [false, Validators.required],
  });

  open(dimension?: Dimension) {
    if (dimension) {
      this.form.patchValue({
        title: dimension.title,
        description: dimension.description || '',
        isScalar: dimension.isScalar,
        isPricing: dimension.isPricing ?? false,
      });
      this.dimension = dimension;
    } else {
      this.dimension = null;
      this.form.reset({ isScalar: false, isPricing: false });
    }

    this.dialog.open();
  }

  get isSaveDisabled(): boolean {
    // Disable if form is invalid
    if (this.form.invalid) {
      return true;
    }

    return false;
  }

  close(fromClose?: boolean) {
    this.form.reset();
    this.saving = false;

    if (!fromClose) this.dialog.close();
  }

  cancel() {
    this.close();
    this.closed.emit(false);
  }

  async save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value;
    const title = value.title || '';
    if (!title) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;

    const payload: Record<string, unknown> = {
      title,
      description: value.description?.trim() || null,
      isScalar: value.isScalar ?? false,
      isPricing: value.isPricing ?? false,
    };

    const request$ = this.dimension?.id
      ? this.http.patch(`/v1/rating/dimensions/${this.dimension.id}`, payload)
      : this.http.post('/v1/rating/dimensions', payload);

    request$.subscribe({
      next: () => {
        this.toastSvc.show({
          type: 'success',
          title: this.dimension?.id ? this.config.toasts.updateSuccess.title : this.config.toasts.createSuccess.title,
          text: this.dimension?.id
            ? this.config.toasts.updateSuccess.text(title)
            : this.config.toasts.createSuccess.text(title),
        });
        this.close();
        this.closed.emit(true);
      },
      error: (error: { error?: { message?: string } }) => {
        this.toastSvc.show({
          type: 'error',
          title: this.config.toasts.error.title,
          text: error?.error?.message || this.config.toasts.error.defaultText,
        });
        this.saving = false;
      },
    });
  }
}
